export const isValidUrl = (url: string) => {
  try {
    new URL(url)
    return true
  } catch (e) {
    return false
  }
}

export const getUrlFromString = (str: string) => {
  if (isValidUrl(str)) return str
  try {
    if (str.includes('.') && !str.includes(' ')) {
      return new URL(`https://${str}`).toString()
    }
  } catch (_) {}
  return str
}

export const getSearchParams = (url: string) => {
  // Create a params object
  let params = {} as Record<string, string>

  new URL(url).searchParams.forEach(function (val, key) {
    params[key] = val
  })

  return params
}

export const getSearchParamsWithArray = (url: string) => {
  let params = {} as Record<string, string | string[]>

  new URL(url).searchParams.forEach(function (val, key) {
    const existing = params[key]
    if (Array.isArray(existing)) {
      existing.push(val)
    } else if (typeof existing === 'string') {
      params[key] = [existing, val]
    } else {
      params[key] = val
    }
  })

  return params
}

export const UTMTags = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const

export const constructURLFromUTMParams = (
  url: string,
  utmParams: Record<string, string>,
) => {
  if (!url) return ''
  try {
    const newURL = new URL(url)
    for (const [key, value] of Object.entries(utmParams)) {
      if (value === '') {
        newURL.searchParams.delete(key)
      } else {
        newURL.searchParams.set(key, value)
      }
    }
    return newURL.toString()
  } catch (e) {
    return ''
  }
}

export const paramsMetadata = [
  { display: 'Referral (ref)', key: 'ref', examples: 'twitter, facebook' },
  { display: 'UTM Source', key: 'utm_source', examples: 'twitter, facebook' },
  { display: 'UTM Medium', key: 'utm_medium', examples: 'social, email' },
  { display: 'UTM Campaign', key: 'utm_campaign', examples: 'summer_sale' },
  { display: 'UTM Term', key: 'utm_term', examples: 'blue_shoes' },
  { display: 'UTM Content', key: 'utm_content', examples: 'logolink' },
]

export const getUrlWithoutUTMParams = (url: string) => {
  try {
    const newURL = new URL(url)
    paramsMetadata.forEach((param) => newURL.searchParams.delete(param.key))
    return newURL.toString()
  } catch (e) {
    return url
  }
}

export const getPrettyUrl = (url: string) => {
  return url.replace(/(^\w+:|^)\/\//, '').replace('www.', '')
}

/**
 * Given a URL a user typed for something like a nav item, strip it down to a
 * site-relative path if its host matches one of the site's own domains
 * (its custom domain or its `{slug}.blgg.dev`-style subdomain). Anything
 * that isn't recognized as one of those hosts — including URLs already
 * relative, or external URLs — is returned unchanged.
 */
export const toRelativeSiteUrl = (
  url: string | null | undefined,
  siteHosts: Array<string | null | undefined>,
): string => {
  if (!url) return url ?? ''
  const value = url.trim()
  if (!value) return value

  const hosts = Array.from(
    new Set(
      siteHosts
        .filter((host): host is string => !!host)
        .map((host) => host.toLowerCase()),
    ),
  )
  if (hosts.length === 0) return value

  const hasProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(value)

  let parsed: URL
  try {
    parsed = new URL(hasProtocol ? value : `https://${value}`)
  } catch {
    return value
  }

  // Use `host` (not `hostname`) so hosts with an explicit port — e.g. local
  // dev subdomains like `acme.localhost:7980` — are matched correctly.
  const host = parsed.host.toLowerCase()
  const isSiteHost = hosts.some(
    (siteHost) => host === siteHost || host === `www.${siteHost}`,
  )
  if (!isSiteHost) return value

  return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/'
}
